import React from 'react';

import { Flex, Image } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { openCreateOKRPanel } from 'reducers/createObjectivePanelSlice';
import { useProfilePage } from 'screens/Profile/hooks/dataHooks';
import { useGoalsCascadedViewData } from 'screens/Team/Rabbit/Goals/AgGridTreeView/hooks/dataHooks';
import { useTimeBoundTimePeriods } from 'screens/home/summary/components/Cards/Goals/hooks';
import { useOneOnOneInstanceDetails } from 'screens/oneOnOneRabbit/InstancePage/hooks/dataHooks';
import useOneOnOnePageParams from 'screens/oneOnOneRabbit/hooks/useOneOnOnePageParams';
import { selectLoggedInEmployeeId } from 'selectors/loggedInEmployee';
import { okrsPageSelector } from 'selectors/okrsPage';

import { GoalIconDualTone, Plus } from 'assets/assetsV1/iconComponents';
import NoGoalsImage from 'assets/assetsV1/illustrations/noGoals.svg';

import { OKR_TYPES } from 'constants/ProjectConstants';
import { modifyString, StringModifier } from 'constants/copyHelpers';
import { sendClickedOnCreateKRevent, sendCreatedKREvent, sendOpenedKRPanelEvent } from 'constants/mixpanelHelper';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

import { Button } from 'uiToolkitV1/Button';
import { SkeletonLoader } from 'uiToolkitV1/SkeletonLoader';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { SidebarCardTemplate } from '../../common/components/SidebarCardTemplate';
import { GoalsOneOnOneAgGrid } from './components/GoalsOneOnOneAgGrid';

const GROUP_BY = {
  uuid: null,
  keyName: 'objective',
  displayName: 'Objective',
  enabled: true,
};

const SPLIT_BY = {
  uuid: null,
  keyName: 'none',
  displayName: 'None',
  enabled: true,
};

const GoalsSection = () => {
  const location = useLocation();
  const history = useHistory();
  const { masterId, instanceId } = useOneOnOnePageParams();

  const { receiverEmployee } = useOneOnOneInstanceDetails({
    masterId,
    instanceId,
  });

  const { id: employeeId, displayName } = receiverEmployee;

  const { data: employee } = useProfilePage({ employeeId });

  const loggedInEmployeeId = useSelector(selectLoggedInEmployeeId);

  const isLoggedInEmployeeTheReceiver = loggedInEmployeeId === employeeId;

  const { data: timelineData } = useTimeBoundTimePeriods();

  const sideNavSelectedCohort = null;

  const dispatch = useDispatch();

  const { selectedTimelines } = useSelector(okrsPageSelector);

  const { data, isLoading } = useGoalsCascadedViewData({
    attributeFilter: {
      filterConditions: [
        {
          type: 'NAME',
          operatorType: 'IN',
          data: [employeeId],
        },
        {
          type: 'TYPE',
          operatorType: 'IN',
          data: ['GOAL'],
        },
      ],
    },
    cohort: sideNavSelectedCohort,
    groupByKeys: GROUP_BY,
    splitByKeys: SPLIT_BY,
    orderBy: null,
    timeBounds: timelineData?.current ? [timelineData?.current] : [null],
    isHierarchyView: false,
    pageType: 'PROFILE_PAGE',
  });

  const queryClient = useQueryClient();

  const refetchCascadedViewData = () => {
    queryClient.invalidateQueries([
      REACT_QUERY_KEYS.GoalsCascadedData,
      sideNavSelectedCohort?.id,
      GROUP_BY,
      SPLIT_BY,
      null,
      [timelineData?.current],
      {
        filterConditions: [
          {
            type: 'NAME',
            operatorType: 'IN',
            data: [employeeId],
          },
          {
            type: 'TYPE',
            operatorType: 'IN',
            data: ['GOAL'],
          },
        ],
      },
      false,
    ]);
  };

  /*  read keyResultsName and objectivesName from new config */
  const { keyResultsName, moduleName } = useOrganisationSettings();

  const addNewKRHandler = () => {
    sendClickedOnCreateKRevent({
      eventProperties: { source: '1:1 instance', page: 'One on one Instance' },
    });
    sendOpenedKRPanelEvent({
      eventProperties: { source: '1:1 instance', page: 'One on one Instance', type: 'Add KR Panel' },
    });
    dispatch(
      openCreateOKRPanel({
        selectedObjective: null,
        selectedTimeBoundPeriod: selectedTimelines?.[0],
        okrPanelType: OKR_TYPES.KEY_RESULT,
        onKeyResultCreation: (okrDetails) => {
          sendCreatedKREvent({
            eventProperties: {
              source: '1:1 instance',
              page: 'One on one Instance',
              goalId: okrDetails?.uuid,
            },
          });
          refetchCascadedViewData();
        },
        onObjectiveCreation: refetchCascadedViewData,
        ownerEmployee: {
          email: employee?.email,
          id: employee?.id,
          displayName: employee?.displayName,
          profilePhotoSrc: employee?.profilePhotoSrc,
          designation: employee?.designation,
          employeeFunction: employee?.employeeFunction,
          ownerType: 'OWNER',
        },
        history,
        location,
      })
    );
  };

  if (isLoading) {
    return <SkeletonLoader />;
  }

  if (!data?.length) {
    return (
      <SidebarCardTemplate label={modifyString(moduleName, StringModifier.PLURAL_TITLE_CASE)} Icon={GoalIconDualTone}>
        <Flex mt="24px" flexDir="column" alignItems="center" justifyContent="center">
          <Image width="172px" src={NoGoalsImage as unknown as string} />
          <Flex w="245px" textAlign="center" mt="22px" flexDir="column" p="12px">
            <TextBodyMedium bold>
              {isLoggedInEmployeeTheReceiver
                ? "You haven't created any goals yet"
                : `No goals have been added for ${displayName}`}
            </TextBodyMedium>
            <TextBodyMedium color="text.g2">
              {isLoggedInEmployeeTheReceiver
                ? 'In the beginners mind, there are only possibilities. Create a goal, make possibilities real.'
                : `Add goals for ${displayName}`}
            </TextBodyMedium>
          </Flex>
        </Flex>
        <Button onClick={addNewKRHandler} StartIcon={Plus} alignSelf="center" mt="22px">
          Add New {keyResultsName}
        </Button>
      </SidebarCardTemplate>
    );
  }

  return (
    <SidebarCardTemplate
      label={modifyString(moduleName, StringModifier.PLURAL_TITLE_CASE)}
      Icon={GoalIconDualTone}
      // eslint-disable-next-line react/no-unstable-nested-components
      AddButton={() => (
        <Button StartIcon={Plus} variant="outlined" onClick={addNewKRHandler} data-testid="sidebar-add-new-goal" />
      )}
    >
      <GoalsOneOnOneAgGrid refetchCascadedViewData={refetchCascadedViewData} data={data} />
    </SidebarCardTemplate>
  );
};

export { GoalsSection };
